import {
  BlankContent,
  Card,
  HeadingText,
  Spinner,
  StatData,
} from '@cint/wonderment';
import { useMemo } from 'react';

import { formatCurrencyValue } from '@/utils/formatCurrencyValue';

interface MetricsProps {
  isMarketingCampaignDataLoading: boolean;
  marketingCampaignDataError: boolean;
  // todo: update below types when available
  marketingCampaignData: any;
}

export const Metrics = ({
  isMarketingCampaignDataLoading,
  marketingCampaignData,
  marketingCampaignDataError,
}: MetricsProps) => {
  const totalCompletes = useMemo(() => {
    const sum = (marketingCampaignData?.profitability_by_sources ?? []).reduce(
      (acc, { total_completes }) => acc + total_completes,
      0
    );
    return sum;
  }, [marketingCampaignData?.profitability_by_sources]);

  const currency =
    marketingCampaignData?.profitability_by_sources?.[0]?.total_revenue
      ?.currency_code;

  const totalRevenue = useMemo(() => {
    const sum = (marketingCampaignData?.profitability_by_sources ?? []).reduce(
      (acc, { total_revenue }) => acc + (total_revenue?.value ?? 0),
      0
    );
    return sum;
  }, [marketingCampaignData?.profitability_by_sources]);
  const formattedTotalRevenue = formatCurrencyValue(
    totalRevenue,
    marketingCampaignData?.profitability_by_sources?.[0]?.total_revenue
      ?.currency_scale,
    currency
  );

  const totalCost = useMemo(() => {
    const sum = (marketingCampaignData?.profitability_by_sources ?? []).reduce(
      (acc, { total_costs }) => acc + (total_costs?.value ?? 0),
      0
    );
    return sum;
  }, [marketingCampaignData?.profitability_by_sources]);
  const formattedTotalCost = formatCurrencyValue(
    totalCost,
    marketingCampaignData?.profitability_by_sources?.[0]?.total_costs
      ?.currency_scale,
    currency
  );

  const totalProfitability = useMemo(() => {
    const sum = (marketingCampaignData?.profitability_by_sources ?? []).reduce(
      (acc, { current_profitability }) =>
        acc + (current_profitability?.value ?? 0),
      0
    );
    return sum;
  }, [marketingCampaignData?.profitability_by_sources]);

  const formattedTotalProfitability = formatCurrencyValue(
    totalProfitability,
    marketingCampaignData?.profitability_by_sources?.[0]?.current_profitability
      ?.currency_scale,
    currency
  );

  return (
    <div className="mt-8">
      <Card className="p-6">
        <HeadingText variant="h6" fontWeight="normal">
          Financial insights
        </HeadingText>
        {isMarketingCampaignDataLoading && <Spinner />}
        {marketingCampaignDataError && (
          <BlankContent
            description="Please try again later"
            size="sm"
            title="Something went wrong"
          />
        )}
        {marketingCampaignData?.profitability_by_sources && (
          <>
            <div className="grid grid-cols-4 mt-4">
              <StatData
                labelPosition="above"
                alignment="left"
                labelProps={{
                  label: 'Total completes',
                }}
                size="2xl"
                value={`${totalCompletes}`}
              />
              <StatData
                labelPosition="above"
                alignment="left"
                labelProps={{
                  label: 'Total revenue',
                }}
                size="2xl"
                value={`${formattedTotalRevenue}`}
              />
              <StatData
                labelPosition="above"
                alignment="left"
                labelProps={{
                  label: 'Total cost',
                }}
                size="2xl"
                value={`${formattedTotalCost}`}
              />
              <StatData
                labelPosition="above"
                alignment="left"
                labelProps={{
                  label: 'Current profitability',
                }}
                size="2xl"
                value={`${formattedTotalProfitability}`}
                variant={totalProfitability < 0 ? 'danger' : 'default'}
              />
            </div>
          </>
        )}
      </Card>
    </div>
  );
};
